/**
 * What the customer app's category rails look like.
 *
 * These rails — "What do you need?", "Care by type", "Services" — used to be hardcoded in the
 * apps: the names, the order, and the artwork all baked in, so renaming "Groomers" to
 * "Grooming & spa" meant a release on two platforms.
 *
 * The module LIST is not editable here and shouldn't be: modules are wired through the schema,
 * and a rail pointing at a module no vendor can join would be worse than a badly named one. So
 * there is no Add and no Delete — only the four things that are genuinely presentation.
 */
import { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, Image as ImageIcon, Layers, Loader2, Pencil, Save, X } from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ErrorState } from '@/components/ui/error-state';
import { notify } from '@/components/ui/toaster';
import { use_modules, use_update_module, use_upload_module_icon } from '@/features/modules/hooks';
import type { ModulePresentation } from '@/features/modules/types';

function build_metrics(rows: ModulePresentation[] | undefined): Metric[] {
  const visible = rows?.filter((r) => r.is_visible) ?? [];
  const without = rows?.filter((r) => !r.icon_path) ?? [];
  return [
    {
      key: 'shown',
      label: 'Shown to customers',
      value: rows ? String(visible.length) : undefined,
      hint: rows && rows.length > visible.length ? `${rows.length - visible.length} hidden` : 'None hidden',
      icon: Eye,
      loading: !rows,
    },
    {
      key: 'no_icon',
      label: 'Missing an icon',
      // The work, made visible — otherwise nobody knows the rails are running on placeholders.
      value: rows ? String(without.length) : undefined,
      hint: without.length ? 'The app draws a placeholder' : 'Every module has one',
      tone: without.length ? 'warning' : 'default',
      icon: ImageIcon,
      loading: !rows,
    },
  ];
}

export function ModulePage() {
  const { data: rows, isLoading, isError, refetch } = use_modules();
  const [editing, set_editing] = useState<string | null>(null);
  const update = use_update_module();

  const metrics = useMemo(() => build_metrics(rows), [rows]);
  const ordered = useMemo(
    () => [...(rows ?? [])].sort((a, b) => a.display_order - b.display_order),
    [rows],
  );

  /** Swap two neighbours' positions — the whole reordering interaction. */
  async function move(row: ModulePresentation, direction: -1 | 1) {
    const index = ordered.findIndex((r) => r.id === row.id);
    const swap = ordered[index + direction];
    if (!swap) return;
    try {
      await update.mutateAsync({ module_type: row.module_type, input: { display_order: swap.display_order } });
      await update.mutateAsync({ module_type: swap.module_type, input: { display_order: row.display_order } });
    } catch {
      notify.error('Could not reorder these.');
    }
  }

  async function toggle(row: ModulePresentation) {
    try {
      await update.mutateAsync({ module_type: row.module_type, input: { is_visible: !row.is_visible } });
      notify.success(row.is_visible ? `${row.label} hidden` : `${row.label} is back`, {
        description: row.is_visible
          ? 'Customers no longer see it on the rails. Vendors in it are untouched.'
          : 'It is on the customer rails again.',
      });
    } catch {
      notify.error('Could not change this.');
    }
  }

  return (
    <DashboardLayout
      title="App categories"
      subtitle="The rails customers tap on the home, health and services tabs"
    >
      <MetricStrip metrics={metrics} />

      {isError ? (
        <ErrorState
          title="Could not load the modules"
          action={<Button onClick={() => void refetch()}>Try again</Button>}
        />
      ) : (
        <Card className="overflow-hidden p-0" data-testid="module-list">
          <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span className="font-heading text-data font-bold">Modules</span>
            <span className="text-micro text-muted-foreground">
              {isLoading ? 'Loading…' : `${ordered.length} · customers see them in this order`}
            </span>
          </div>

          <ul className="divide-y divide-border">
            {ordered.map((row, i) => (
              <li key={row.id} data-testid={`module-row-${row.module_type}`}>
                <div className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 lg:grid-cols-[2.5rem_16rem_1fr_auto]">
                  {/* Artwork, or a marker that says a person still has to do something. */}
                  {row.icon_path ? (
                    <AuthImage
                      path={row.icon_path}
                      alt=""
                      className="h-9 w-9 rounded-input object-cover"
                      data-testid={`module-icon-${row.module_type}`}
                    />
                  ) : (
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-input border border-dashed border-warning/50 text-[9px] font-semibold uppercase leading-none text-warning"
                      title="This module has no icon yet"
                      data-testid={`module-icon-missing-${row.module_type}`}
                    >
                      Add
                    </span>
                  )}

                  <div className="flex min-w-0 flex-col">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-heading text-data font-bold">{row.label}</span>
                      {!row.is_visible && (
                        <Badge tone="neutral" size="sm" dot data-testid={`module-hidden-${row.module_type}`}>
                          Hidden
                        </Badge>
                      )}
                    </span>
                    {/* The enum value, shown and never editable — it is what vendors are filed under. */}
                    <span className="truncate font-mono text-micro text-muted-foreground">
                      {row.module_type}
                    </span>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {row.is_visible ? 'On the customer rails' : 'Not advertised — vendors in it are unaffected'}
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === 0 || update.isPending}
                      onClick={() => void move(row, -1)}
                      aria-label={`Move ${row.label} up`}
                      data-testid={`module-up-${row.module_type}`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={i === ordered.length - 1 || update.isPending}
                      onClick={() => void move(row, 1)}
                      aria-label={`Move ${row.label} down`}
                      data-testid={`module-down-${row.module_type}`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => set_editing(editing === row.id ? null : row.id)}
                      data-testid={`module-edit-${row.module_type}`}
                      aria-expanded={editing === row.id}
                    >
                      {editing === row.id ? (
                        <>
                          <X className="mr-1 h-4 w-4" /> Close
                        </>
                      ) : (
                        <>
                          <Pencil className="mr-1 h-4 w-4" />
                          {row.icon_path ? 'Edit' : 'Add icon'}
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void toggle(row)}
                      disabled={update.isPending}
                      aria-label={row.is_visible ? `Hide ${row.label}` : `Show ${row.label}`}
                      data-testid={`module-toggle-${row.module_type}`}
                    >
                      {row.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {editing === row.id && <EditRow row={row} on_done={() => set_editing(null)} />}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </DashboardLayout>
  );
}

function EditRow({ row, on_done }: { row: ModulePresentation; on_done: () => void }) {
  const update = use_update_module();
  const upload = use_upload_module_icon();
  const file_input = useRef<HTMLInputElement>(null);
  const [label, set_label] = useState(row.label);
  const [error, set_error] = useState<string | null>(null);

  async function save() {
    set_error(null);
    if (!label.trim()) return set_error('Give it a name customers will read.');
    try {
      // One save for both, or you get a rename applied with the artwork left behind.
      const file = file_input.current?.files?.[0];
      const icon = file ? await upload.mutateAsync(file) : null;
      await update.mutateAsync({
        module_type: row.module_type,
        input: { label: label.trim(), ...(icon ? { icon_s3_key: icon.icon_s3_key } : {}) },
      });
      notify.success(`${label.trim()} updated`, {
        description: 'The apps pick this up straight away — no release needed.',
      });
      on_done();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Could not save this.');
    }
  }

  const busy = update.isPending || upload.isPending;

  return (
    <div
      className="border-t border-border bg-muted/30 px-3.5 py-3"
      data-testid={`module-editor-${row.module_type}`}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor={`label-${row.id}`}>What customers call it</Label>
          <input
            id={`label-${row.id}`}
            autoFocus
            className="console-field w-64"
            value={label}
            onChange={(e) => set_label(e.target.value)}
            data-testid={`module-label-${row.module_type}`}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`icon-${row.id}`}>{row.icon_path ? 'Replace icon' : 'Add an icon'}</Label>
          <input
            id={`icon-${row.id}`}
            ref={file_input}
            type="file"
            accept="image/*"
            className="console-field w-64 text-micro"
            data-testid={`module-icon-input-${row.module_type}`}
          />
          {!row.icon_path && (
            <span className="text-micro text-warning">Without one the app draws its own placeholder.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void save()} disabled={busy} data-testid={`module-save-${row.module_type}`}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={on_done}>
            Cancel
          </Button>
        </div>
      </div>
      {error && (
        <p className="mt-2 text-micro font-medium text-destructive" data-testid={`module-error-${row.module_type}`}>
          {error}
        </p>
      )}
    </div>
  );
}
